import DialogComponentWrapper from "../Utils/DialogComponentWrapper";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { generateCronConfig, generateCronExpression } from "./utils";
import { FormikForm } from "./ReportForm";
import { useLocation } from "react-router";
import RestAPI from "@aws-amplify/api-rest";
import { toast } from 'react-toastify';

const getFarm = (location) => {
    const params = new URLSearchParams(location && location.search);
    const farm = params.get('farm');
    return farm;
}

/**
* @typedef {{ 
*   nombre: string,
*   tipo_reporte: string,
*   periodo_reporte: string,
*   freq: string,
*   day: number,
*   monthly_check: "last-day" | "day" | "day-of-week",
*   mails_envio_reporte: string,
*   time: string,
*   day_of_week: number,
*   occurrence_in_week: string,
*   day_of_month: number,
*   sectores: number[],
*   equipos: number[],
*   campos: number[]
* }} ReportForm
*/

const ReportDialog = ({ dialogState, setDialogState, dataFarm, sectorsInfo, accountsData, fields, usuarioInfo }) => {
    const { t, i18n } = useTranslation();
    const formikRef = useRef(null);
    const [errorEmail, setErrorEmail] = useState(null);
    const location = useLocation();
    const farmId = getFarm(location);

    useEffect(() => {
        if (!dialogState?.data){
            formikRef?.current.resetForm();
        } 
    }, [dialogState?.data]);

    const onSubmit = ({ sectores, equipos, campos, ...otherValues }, props, actions) => {
        actions.resetForm();
        setDialogState({ visible: false });
        // prepare data to send bulk edit
        if (props.bulkInfo?.bulkEnabled){
            const body = props.bulkInfo.selectedRows.map(editedRow => {
                const editedReporte = {
                    nombre: editedRow.nombre,
                    cuenta_id: editedRow.cuenta_id,
                    campo_id: editedRow.campo_id,
                    parametros: editedRow.parametros,
                    configuracion: editedRow.configuracion,
                    esta_habilitado: true,  
                }
                switch (props.bulkInfo.fieldToEdit) {
                    case "mails_envio_reporte":
                        editedReporte.configuracion.destinatarios = otherValues.mails_envio_reporte;
                        break;
                    default:
                        break;
                }
                return {
                    id: editedRow.reporte_id,
                    values: editedReporte
                }
            });
            const endpoint = `/farms/${farmId}/reporte/bulk`
            RestAPI.post('apiAdminDropControl', endpoint, { body }).then(() => {
                toast.success(t("forms.toastSuccess"), { position: toast.POSITION.TOP_RIGHT, autoClose: 6000 });
                if(typeof dialogState?.setReFetch === 'function'){
                    dialogState?.setReFetch(p => p + 1);
                }
            }).catch((error) => {
                console.log(error);
                toast.error(t("forms.toastError"), { position: toast.POSITION.TOP_RIGHT, autoClose: 6000 });
            });
        } else {
            // single (no bulk) create/edit
            const cron_config = generateCronConfig(otherValues);
            const expression = generateCronExpression(cron_config);
            const isDeseret = otherValues.tipo_reporte === "DESERET";
            const body = {
                nombre: otherValues.nombre,
                cuenta_id: isDeseret ? dataFarm.cuenta_id : null,
                campo_id: isDeseret ? null : dataFarm.campo_id,
                parametros: {
                    tipo_reporte: otherValues.tipo_reporte,
                    periodo_reporte: otherValues.periodo_reporte
                },
                configuracion: {
                    destinatarios: otherValues.mails_envio_reporte,
                    cron_config,
                    expression,
                    timezone: dataFarm.huso_horario ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
                    fecha_ultimo_envio: null,
                    locale: i18n.language,
                    usuario_id: dialogState.editMode ? null : usuarioInfo.usuario_id // we dont care about who created it in edit mode
                },
                esta_habilitado: true
            }
            if (sectores?.length) body.parametros.sectores = sectores;
            if (equipos?.length) body.parametros.equipos = equipos;
            if (campos?.length) body.parametros.campos = campos;
            if (dialogState.editMode){
                const endpoint = `/farms/${farmId}/reporte/${dialogState?.reporteId}/editar`
                RestAPI.post('apiAdminDropControl', endpoint, { body }).then((data) => {
                    if (data.noChanges){
                        toast.info(t("forms.toastInfo"), { position: toast.POSITION.TOP_RIGHT, autoClose: 6000 });
                        return;
                    }
                    toast.success(t("forms.toastSuccess"), { position: toast.POSITION.TOP_RIGHT, autoClose: 6000 });
                    dialogState.setReFetch(p => p + 1);
                }).catch(() => {
                    toast.error(t("forms.toastError"), { position: toast.POSITION.TOP_RIGHT, autoClose: 6000 });
                });
            } else {
                const endpoint = `/farms/${farmId}/reporte/crear`;
                RestAPI.post('apiAdminDropControl', endpoint, { body }).then(() => {
                    toast.success(t("forms.toastSuccess"), { position: toast.POSITION.TOP_RIGHT, autoClose: 6000 });
                    dialogState.setReFetch(p => p + 1);
                }).catch(() => {
                    toast.error(t("forms.toastError"), { position: toast.POSITION.TOP_RIGHT, autoClose: 6000 });
                });
            }
        }

    }

    return (
        <div>
            <DialogComponentWrapper 
                formikRef={formikRef} 
                containerId="reports-form"
                t={t} 
                dialogState={dialogState} 
                setDialogState={setDialogState}
                header={t("automaticReports.reporteTitle")}
                width={"800px"}
                allowDragging={false}
            >
                <FormikForm
                    innerRef={formikRef}
                    edit={true}
                    data={dialogState?.data}
                    submit={onSubmit}
                    emailChipsState={{errorEmail, setErrorEmail}}
                    dataFarm={dataFarm}
                    sectorsInfo={sectorsInfo}
                    accountsData={accountsData}
                    fields={fields}
                    bulkInfo={dialogState?.bulkInfo} // { bulkEnabled, fieldToEdit, selectedRows }
                />
            </DialogComponentWrapper>
        </div>
    )
}

export { ReportDialog }